import type { IInputProps } from 'components/form/input/types'
import type { FormFieldRef, TFormFieldDict } from 'components/form/types/field'
import type { IForm } from 'components/form/types/form'
import type { TFormModel } from 'components/form/types/formModel'
import { isRef, type ComputedRef, type Ref } from 'vue'
import { select } from '@/utils/object'
import { evaluateFormState, evaluateReactiveProps, getFormFieldPath, isRenderableField } from './utils'
import { isFactor, isFunction } from '@/utils/is'
import { toBoolean } from '@/utils/boolean'
import type { StringIndexable } from '@/types/object'
import type { UseFormErrorsReturn } from './useFormErrors'
import { unrefShallow } from '@/utils/reactive'
import type { UseFormProps } from '.'

export type UseFormDepsProps<T extends TFormModel = { [key: string]: any }, ContextType extends (StringIndexable | undefined) = StringIndexable> = UseFormProps<T, ContextType> & {
  fields: Ref<TFormFieldDict<T>> | ComputedRef<TFormFieldDict<T>>
  onDependencyUpdate?: (field: FormFieldRef) => void
  errors: UseFormErrorsReturn<T>;
  context?: Ref<ContextType>;
}

export const useFormDeps = <T extends TFormModel = { [key: string]: any }, ContextType extends (StringIndexable | undefined) = StringIndexable>(
  props: UseFormDepsProps<T, ContextType>
) => {
  const updateDependants = (fieldSchema: IForm<any>, field?: FormFieldRef) => {
    if (!fieldSchema.$dependants) return;

    for (const dep of fieldSchema.$dependants) {
      const a = select<TFormFieldDict<T>, IInputProps>(props.fields.value, dep)
      const aRef = select<TFormFieldDict<T>, FormFieldRef>(
        props.fields.value,
        dep,
        false
      )
      const b = select<IForm<T, ContextType>, IForm<T, ContextType>>(unrefShallow(props.schema) as any, dep)

      if (b && !isRenderableField(b)) {
        if (a && (a as any).$ref) {
          const blockRef = (a as any).$ref as Ref<typeof a>

          if (isFunction(b.$hidden)) {
            blockRef.value.hidden = toBoolean(
              b.$hidden({
                state: evaluateFormState<T, ContextType>(unrefShallow(props.schema) as any, props.fields.value),
                fields: props.fields.value,
                context: props.context,
                path: getFormFieldPath(field) || getFormFieldPath(blockRef) || ''
              })
            )
          }
          continue
        } else {
          console.warn(`bad field dependancy ${dep}`)
          continue
        }
      }

      if (a && b) {
        if (aRef && isRef(aRef)) {
          evaluateReactiveProps(b, aRef, unrefShallow(props.schema) as any, props.fields.value, props.context, field)
        }

        if (aRef && isRef(aRef) && props.onDependencyUpdate) {
          props.onDependencyUpdate(aRef)
        }
      } else {
        console.error(`Invalid path to dependant field: '${dep}'`)
      }

      //if (a && field && field.value.type === 'boolean') {
      //  const x = toBoolean(field.value.value)
      //  if (x === false) a.value = undefined;
      //}
    }
  }

  const updateDeps = <K extends StringIndexable = T, ContextType extends (StringIndexable | undefined) = StringIndexable>(sch: IForm<K, ContextType>) => {
    for (const [key, value] of Object.entries(sch)) {
      if (isFactor(value)) continue
      if (typeof value !== 'object') continue

      if (!(sch as any)[key]) continue;
      updateDependants(updateDeps<any>((sch as any)[key] as IForm<any>))
    }

    return sch
  }

  const clearDependingFields = (calleeField: FormFieldRef, fieldSchema: IForm<any>) => {
    if (!fieldSchema.$clearFieldsIfNotTrue) return
    if (calleeField.value.type !== 'boolean') return

    for (const dep of fieldSchema.$clearFieldsIfNotTrue) {
      const a = select<TFormFieldDict<T>, FormFieldRef>(
        props.fields.value,
        dep,
        false
      )
      if (!a) continue
      const x = calleeField.value.value
      if (x === false) {
        a.value.value = undefined
        if (a.value.schema) {
          updateDependants(a.value.schema, a)
        }
      }
    }
  }

  return { updateDependants, updateDeps, clearDependingFields }
}

export type UseFormDepsReturn<T extends StringIndexable = StringIndexable, ContextType extends (StringIndexable | undefined) = StringIndexable> = ReturnType<
  typeof useFormDeps<T, ContextType>
>
